import React, { useEffect, useState, useCallback } from 'react';
import { axiosInstance } from '../App';
import { toast } from 'sonner';
import {
  ShieldAlert, Server, Archive, RotateCcw, Sparkles, Rocket,
  RefreshCw, CheckCircle2, AlertTriangle, XCircle, ArrowLeft,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const STATUS_CHIP = {
  PASS:     'bg-emerald-100 text-emerald-800 border-emerald-200',
  WARNING:  'bg-amber-100 text-amber-800 border-amber-200',
  CRITICAL: 'bg-rose-100 text-rose-800 border-rose-200',
};

const Section = ({ icon: Icon, title, subtitle, actions, children, testid }) => (
  <div className="bg-white border border-gray-200 rounded-xl overflow-hidden" data-testid={testid}>
    <div className="px-4 py-3 border-b bg-gray-50 flex items-center justify-between">
      <div className="flex items-center gap-2">
        <div className="p-2 rounded-lg bg-indigo-50"><Icon className="w-4 h-4 text-indigo-700" /></div>
        <div>
          <h3 className="font-bold text-gray-900 text-sm">{title}</h3>
          {subtitle && <p className="text-[11px] text-gray-500">{subtitle}</p>}
        </div>
      </div>
      <div className="flex items-center gap-2">{actions}</div>
    </div>
    <div className="p-4">{children}</div>
  </div>
);

const Btn = ({ onClick, loading, children, testid, variant = 'primary' }) => {
  const styles = {
    primary: 'bg-indigo-600 hover:bg-indigo-700 text-white',
    danger:  'bg-rose-600 hover:bg-rose-700 text-white',
    ghost:   'border border-gray-200 text-gray-700 hover:bg-gray-50',
  }[variant];
  return (
    <button
      onClick={onClick}
      disabled={loading}
      data-testid={testid}
      className={`px-3 py-1.5 rounded-lg text-xs font-semibold inline-flex items-center gap-1 ${loading ? 'opacity-50' : styles}`}
    >
      {loading ? <RefreshCw className="w-3 h-3 animate-spin" /> : null}
      {children}
    </button>
  );
};

const CheckRow = ({ check }) => {
  const Icon = check.status === 'PASS' ? CheckCircle2 : check.status === 'WARNING' ? AlertTriangle : XCircle;
  const color = check.status === 'PASS' ? 'text-emerald-600' : check.status === 'WARNING' ? 'text-amber-600' : 'text-rose-600';
  return (
    <div className="flex items-start gap-2 py-1.5 text-xs">
      <Icon className={`w-4 h-4 ${color} shrink-0 mt-0.5`} />
      <span className="font-mono text-gray-700">{check.name}</span>
      <span className="text-gray-600 flex-1">{check.message}</span>
    </div>
  );
};

const Hardening = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState({});
  const [secretAudit, setSecretAudit] = useState(null);
  const [envAudit, setEnvAudit] = useState(null);
  const [retention, setRetention] = useState(null);
  const [recovery, setRecovery] = useState(null);
  const [restore, setRestore] = useState(null);
  const [gateResult, setGateResult] = useState(null);

  const setBusy = (k, v) => setLoading(s => ({ ...s, [k]: v }));

  const loadAll = useCallback(async () => {
    setBusy('all', true);
    try {
      const [s, e, r, rec, last] = await Promise.all([
        axiosInstance.get('/hardening/secret-audit').catch(() => null),
        axiosInstance.get('/hardening/env-audit').catch(() => null),
        axiosInstance.get('/hardening/retention/totals').catch(() => null),
        axiosInstance.get('/hardening/recovery-readiness').catch(() => null),
        axiosInstance.get('/hardening/restore/latest').catch(() => null),
      ]);
      setSecretAudit(s?.data || null);
      setEnvAudit(e?.data || null);
      setRetention(r?.data || null);
      setRecovery(rec?.data || null);
      setRestore(last?.data || null);
    } finally {
      setBusy('all', false);
    }
  }, []);

  useEffect(() => { const init = async () => { await loadAll(); }; init(); }, [loadAll]);

  const runRetention = async () => {
    setBusy('retention', true);
    try {
      const r = await axiosInstance.post('/hardening/retention/apply');
      toast.success(`Promoted ${r.data.promoted_weekly + r.data.promoted_monthly}, deleted ${r.data.deleted_count}`);
      setRetention(r.data.totals);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed');
    } finally { setBusy('retention', false); }
  };

  const runRestore = async () => {
    setBusy('restore', true);
    try {
      const r = await axiosInstance.post('/hardening/restore/validate');
      setRestore(r.data);
      if (r.data.status === 'PASS') toast.success(`Restore validated — ${r.data.documents_restored} docs`);
      else toast.warning(`Restore status: ${r.data.status}`);
      loadAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed');
    } finally { setBusy('restore', false); }
  };

  const runGate = async () => {
    setBusy('gate', true);
    try {
      const r = await axiosInstance.post('/hardening/deploy/gate');
      setGateResult({ ok: true, ...r.data });
      toast.success('Deploy gate: PRODUCTION READY');
    } catch (err) {
      setGateResult({ ok: false, ...(err?.response?.data?.detail || {}) });
      toast.error('Deploy gate FAILED');
    } finally { setBusy('gate', false); }
  };

  return (
    <div className="space-y-5 max-w-6xl mx-auto" data-testid="hardening-page">
      <div className="flex items-center justify-between">
        <button
          onClick={() => navigate('/settings')}
          className="inline-flex items-center gap-1 text-sm text-gray-600 hover:text-gray-900"
          data-testid="back-to-settings"
        >
          <ArrowLeft className="w-4 h-4" /> Settings
        </button>
        <button onClick={loadAll} disabled={loading.all} className="px-3 py-2 rounded-lg border-2 border-gray-200 text-sm hover:bg-gray-50 inline-flex items-center gap-1">
          <RefreshCw className={`w-4 h-4 ${loading.all ? 'animate-spin' : ''}`} /> Refresh All
        </button>
      </div>

      <div>
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <ShieldAlert className="w-7 h-7 text-indigo-600" /> Production Hardening
        </h1>
        <p className="text-sm text-gray-500 mt-1">
          P11.5 — Pre-deploy and runtime safety checks: secrets, environment, backups, restore validation, deploy gate.
        </p>
      </div>

      {/* Recovery Readiness top card */}
      {recovery && (
        <div className="bg-gradient-to-r from-indigo-50 to-purple-50 border-2 border-indigo-200 rounded-xl p-6 flex items-center justify-between" data-testid="recovery-card">
          <div className="flex items-center gap-3">
            <Sparkles className="w-10 h-10 text-indigo-700" />
            <div>
              <h2 className="text-xl font-bold text-indigo-900">Recovery Readiness</h2>
              <p className="text-xs text-indigo-700 mt-1">Backup freshness + retention + restore-test recency + backup-system health</p>
            </div>
          </div>
          <div className="text-right">
            <div className="text-5xl font-bold text-indigo-700" data-testid="recovery-score">
              {recovery.score}<span className="text-2xl text-gray-400">/100</span>
            </div>
            <div className="text-sm font-semibold text-indigo-800">{recovery.grade}</div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Deploy Gate */}
        <Section icon={Rocket} title="Smoke Test Gate" subtitle="CI/CD hook — 200 if PRODUCTION_READY, 503 otherwise"
          actions={<Btn onClick={runGate} loading={loading.gate} testid="run-gate-btn">Run Gate</Btn>}
          testid="gate-section">
          {gateResult ? (
            <div className={`p-3 rounded-lg ${gateResult.ok ? 'bg-emerald-50 border border-emerald-200' : 'bg-rose-50 border border-rose-200'}`}>
              <div className="font-bold text-sm" data-testid="gate-result-status">
                {gateResult.ok ? '✅ PRODUCTION READY' : `❌ ${gateResult.status || 'FAIL'}`}
              </div>
              <div className="text-xs text-gray-600 mt-1 font-mono">
                {gateResult.summary && `${gateResult.summary.passed}/${gateResult.summary.total} passed · ${gateResult.duration_ms}ms`}
              </div>
              {gateResult.failed_tests && (
                <div className="text-xs text-rose-700 mt-1">Failed: {gateResult.failed_tests.join(', ')}</div>
              )}
            </div>
          ) : (
            <p className="text-xs text-gray-500 italic">Click &quot;Run Gate&quot; to validate the current deployment.</p>
          )}
        </Section>

        {/* Restore validation */}
        <Section icon={RotateCcw} title="Monthly Restore Validation" subtitle="Restores latest backup into a sandbox DB and verifies key data"
          actions={<Btn onClick={runRestore} loading={loading.restore} testid="run-restore-btn">Validate Restore</Btn>}
          testid="restore-section">
          {restore?.status ? (
            <>
              <div className="flex items-center justify-between mb-2">
                <span className={`px-2 py-1 rounded text-xs font-bold border ${STATUS_CHIP[restore.status] || STATUS_CHIP.WARNING}`}>
                  {restore.status}
                </span>
                {(restore.documents_restored != null || restore.duration_ms != null) && (
                  <span className="text-xs text-gray-500 font-mono">
                    {restore.documents_restored != null ? `${restore.documents_restored} docs` : ''}
                    {restore.documents_restored != null && restore.duration_ms != null ? ' · ' : ''}
                    {restore.duration_ms != null ? `${restore.duration_ms}ms` : ''}
                  </span>
                )}
              </div>
              {restore.filename && (
                <div className="text-[11px] text-gray-500 font-mono break-all" data-testid="restore-filename">{restore.filename}</div>
              )}
              {restore.reason && (
                <div className="mt-2 px-3 py-2 rounded-lg bg-rose-50 border-2 border-rose-200 text-xs text-rose-800" data-testid="restore-fail-reason">
                  <strong className="font-semibold">السبب / Reason:</strong> {restore.reason}
                  {Array.isArray(restore.missing_filenames) && restore.missing_filenames.length > 0 && (
                    <div className="mt-1 text-[10px] font-mono opacity-80">
                      Missing files: {restore.missing_filenames.join(', ')}
                    </div>
                  )}
                  {Array.isArray(restore.trace_tail) && restore.trace_tail.length > 0 && (
                    <details className="mt-1.5">
                      <summary className="cursor-pointer text-[10px] font-semibold opacity-80 select-none">
                        تفاصيل الاستثناء (للمطوّر)
                      </summary>
                      <pre className="mt-1 text-[10px] font-mono opacity-80 whitespace-pre-wrap break-all">
                        {restore.trace_tail.join('\n')}
                      </pre>
                    </details>
                  )}
                </div>
              )}
              {(restore.checks || []).length > 0 && (
                <div className="mt-2 space-y-0">
                  {restore.checks.map((c, i) => <CheckRow key={i} check={c} />)}
                </div>
              )}
            </>
          ) : (
            <p className="text-xs text-gray-500 italic">No restore test recorded yet.</p>
          )}
        </Section>

        {/* Environment audit */}
        <Section icon={Server} title="Environment Audit" subtitle="Runtime configuration risks"
          testid="env-section">
          {envAudit ? (
            <>
              <span className={`inline-block px-2 py-1 rounded text-xs font-bold border mb-2 ${STATUS_CHIP[envAudit.overall_status] || STATUS_CHIP.WARNING}`}>
                {envAudit.overall_status}
              </span>
              <div className="space-y-0">
                {envAudit.checks.map((c, i) => <CheckRow key={i} check={c} />)}
              </div>
            </>
          ) : <p className="text-xs text-gray-500 italic">Loading…</p>}
        </Section>

        {/* Secret audit */}
        <Section icon={ShieldAlert} title="Secret Audit" subtitle="Scans frontend build, .env.example, package files, git history"
          testid="secret-section">
          {secretAudit ? (
            <>
              <span className={`inline-block px-2 py-1 rounded text-xs font-bold border mb-2 ${STATUS_CHIP[secretAudit.summary.overall_status] || STATUS_CHIP.WARNING}`}>
                {secretAudit.summary.overall_status}
              </span>
              <div className="space-y-0">
                {secretAudit.checks.map((c, i) => <CheckRow key={i} check={c} />)}
              </div>
            </>
          ) : <p className="text-xs text-gray-500 italic">Loading…</p>}
        </Section>

        {/* Backup retention */}
        <Section icon={Archive} title="Backup Retention" subtitle="Daily 30 · Weekly 12 · Monthly 12"
          actions={<Btn onClick={runRetention} loading={loading.retention} testid="run-retention-btn">Apply Policy</Btn>}
          testid="retention-section">
          {retention ? (
            <div className="grid grid-cols-4 gap-2">
              <div className="bg-emerald-50 border border-emerald-200 rounded p-2 text-center">
                <div className="text-[10px] text-emerald-700">Daily</div>
                <div className="text-lg font-bold text-emerald-900" data-testid="retention-daily">{retention.daily}</div>
              </div>
              <div className="bg-blue-50 border border-blue-200 rounded p-2 text-center">
                <div className="text-[10px] text-blue-700">Weekly</div>
                <div className="text-lg font-bold text-blue-900" data-testid="retention-weekly">{retention.weekly}</div>
              </div>
              <div className="bg-purple-50 border border-purple-200 rounded p-2 text-center">
                <div className="text-[10px] text-purple-700">Monthly</div>
                <div className="text-lg font-bold text-purple-900" data-testid="retention-monthly">{retention.monthly}</div>
              </div>
              <div className="bg-gray-50 border border-gray-200 rounded p-2 text-center">
                <div className="text-[10px] text-gray-700">Total</div>
                <div className="text-lg font-bold text-gray-900" data-testid="retention-total">{retention.total}</div>
              </div>
            </div>
          ) : <p className="text-xs text-gray-500 italic">Loading…</p>}
        </Section>
      </div>
    </div>
  );
};

export default Hardening;
